import { useState, useEffect } from 'react';
import { landing as landingApi } from '@/lib/api';
import PublicNav, { PublicFooter } from '@/components/PublicNav';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Copy, Bot, Terminal } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const Code = ({ code, title }) => {
  const copy = () => { navigator.clipboard.writeText(code); toast.success('Copied'); };
  return (
    <div className="relative group">
      {title && <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{title}</p>}
      <pre className="bg-slate-900 text-slate-100 rounded-xl p-5 overflow-x-auto text-xs font-mono leading-relaxed border border-slate-800"><code>{code}</code></pre>
      <button onClick={copy} className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-md bg-slate-700 hover:bg-slate-600 text-slate-300"><Copy className="w-3 h-3" /></button>
    </div>
  );
};

export default function AgentGuide() {
  const [footer, setFooter] = useState({});
  useEffect(() => { landingApi.get().then(r => setFooter(r.data?.footer || {})).catch(() => {}); }, []);

  const systemPromptSnippet = `## TENSORNEX PLATFORM INTEGRATION

You are integrating with Tensornex, a GPU-powered AI services platform.
All services are accessed through ONE base URL with ONE API key.

BASE_URL: ${API_BASE}/api/v1     (legacy — inline base64 image_data in Docling responses)
BASE_URL_V2: ${API_BASE}/api/v2  (recommended for Docling with images — returns presigned URLs, avoids MongoDB 16MB failures)
AUTH: X-API-Key header with a gs_live_ prefixed key

## PREREQUISITE STEPS (do these ONCE before any API call)
1. User must have an account (POST /api/auth/signup)
2. User must subscribe to services from the dashboard (select services + duration)
3. User must have an API key (POST /api/keys) — starts with gs_live_

## CRITICAL RULES
- You CANNOT call a service the user hasn't subscribed to → returns 403
- Latency is set at the ACCOUNT level (single tier for the whole account: economy | standard | priority)
  economy = queued at low priority, higher daily limits reserved for premium users
  standard = balanced throughput
  priority = front-of-queue, fastest turnaround, highest per-day limits
- All resource names are auto-namespaced — just pass simple names like "my_docs"
- There are NO client libraries to install — everything is HTTP POST/GET/DELETE
- There are NO connection strings, ports, or per-service credentials
- Point IDs in Qdrant must be unsigned integers or UUIDs — not strings like "doc_001"
- Documents to process must be reachable by URL — upload to MinIO first, then use /storage/presigned
- For image-heavy PDFs (>50 pictures), USE /api/v2/docs/* — returns presigned image URLs instead of inline base64`;

  const endpointSpec = `## COMPLETE ENDPOINT REFERENCE

### Authentication Header (required on ALL requests)
X-API-Key: gs_live_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx

### ML MODELS (stateless — no resources to create)

POST /api/v1/embeddings/embed
  Required service: bge_m3 OR minilm
  Body: { "texts": ["string", ...], "model": "bge_m3"|"minilm", "return_sparse": bool, "return_colbert": bool }
  Returns: { "model": str, "data": { "dense": [[float, ...], ...] }, "count": int }
  Use when: You need vector representations of text for search, clustering, or similarity

POST /api/v1/embeddings/rerank
  Required service: bge_reranker
  Body: { "query": "string", "passages": ["string", ...], "top_k": int }
  Returns: { "results": [{ "index": int, "passage": str, "score": float }] }
  Use when: You have candidate results and need to sort by relevance

POST /api/v1/embeddings/nlp
  Required service: spacy
  Body: { "text": "string" }
  Returns: { "data": { "entities": [{ "text": str, "label": str }], "tokens": [...] } }
  Use when: You need NER, POS tags, or sentence splitting

### QDRANT — Vector Database (all routes: /api/v1/vectors/*)

POST /vectors/collections     → Create collection  { "collection_name": str, "vector_size": 1024|384, "distance": "cosine" }
GET  /vectors/collections     → List collections
POST /vectors/upsert          → Store vectors  { "collection_name": str, "points": [{ "id": str, "vector": [float], "payload": {} }] }
POST /vectors/search          → Search vectors  { "collection_name": str, "vector": [float], "limit": int, "filter": {} }
POST /vectors/delete          → Delete points   { "collection_name": str, "point_ids": [str] }
DELETE /vectors/collections/{name} → Delete collection

### ELASTICSEARCH — Full-Text Search (all routes: /api/v1/search/*)

POST /search/indices          → Create index  { "index_name": str, "mappings": { "properties": { field: { "type": "text" } } } }
POST /search/index            → Index document  { "index_name": str, "doc_id": str, "document": {} }
POST /search/query            → Search  { "index_name": str, "query": { "multi_match": { "query": str, "fields": [str] } }, "size": int }
DELETE /search/index/{name}/doc/{id} → Delete document
DELETE /search/indices/{name}  → Delete index

### NEO4J — Graph Database (all routes: /api/v1/graph/*)

POST /graph/query             → Execute Cypher  { "cypher": str, "parameters": {} }
  Note: client_id is auto-injected. Your nodes are isolated from other clients.

### REDIS — Cache (all routes: /api/v1/cache/*)

POST /cache/set               → Set key  { "key": str, "value": any, "ttl_seconds": int|null }
POST /cache/get               → Get key  { "key": str }  → { "key": str, "value": any, "exists": bool }
POST /cache/delete            → Delete key  { "key": str }
POST /cache/keys              → List keys  { "pattern": "prefix*" }
POST /cache/publish           → Pub/Sub  { "channel": str, "message": any }

### KAFKA — Event Streaming (all routes: /api/v1/streaming/*)

POST /streaming/topics        → Create topic  { "topic": str, "num_partitions": int }
GET  /streaming/topics        → List topics
POST /streaming/produce       → Produce  { "topic": str, "key": str, "value": {} }
POST /streaming/consume       → Consume  { "topic": str, "group_id": str, "max_messages": int }

### MINIO — S3 Storage (all routes: /api/v1/storage/*)
Note: All bucket names are automatically prefixed with your client ID.
Example: you pass "documents" → stored as "client-{your_id}-documents".
Your files are fully isolated — no other customer can access your storage.

POST /storage/buckets         → Create bucket  { "bucket_name": str }
GET  /storage/buckets         → List buckets
POST /storage/upload          → Upload file (multipart form data: bucket_name, object_name, file)
                                Returns: { "status": "uploaded", "bucket_name": str, "object_name": str, "size_bytes": int, "content_type": str }
GET  /storage/download/{bucket}/{path} → Download file (returns binary stream)
POST /storage/list            → List objects  { "bucket_name": str, "prefix": str, "max_keys": int }
POST /storage/presigned       → Presigned URL (internal use only — URL uses internal hostname)
                                { "bucket_name": str, "object_name": str, "method": "GET"|"PUT", "expires_seconds": int }
                                Note: Presigned URLs work when passed to other Tensornex services (e.g., /pdf/parse) but NOT from external networks.
DELETE /storage/objects/{bucket}/{path} → Delete object

### AI SEARCH (Vane + SearXNG) — /api/v1/search/*

POST /search/ai-query        → AI search with cited answer  { "query": str, "mode": "speed"|"balanced"|"quality", "domain": str|null }
POST /search/raw             → Raw web search results  { "query": str, "limit": int, "domain": str|null }

### HEADLESS BROWSER (Lightpanda) — /api/v1/browse/*

POST /browse/render          → Render page, return content  { "url": str, "wait_for": "networkidle", "timeout_ms": int }
POST /browse/extract         → Render + extract by CSS selectors  { "url": str, "selectors": { "title": "h1", "content": "article" } }

### DOCLING (Universal Document AI) — /api/v1/docs/*  (legacy, inline base64)
Supports 20+ input formats: PDF, DOCX, PPTX, XLSX, HTML, CSV, Markdown, LaTeX, images (PNG/JPEG/TIFF/BMP/WEBP),
audio (WAV/MP3/M4A/AAC/OGG/FLAC), video (MP4/AVI/MOV), WebVTT, XBRL, JATS, USPTO.
Output: JSON (with confidence + bounding boxes), Markdown, HTML, text, DocTags, or RAG-ready chunks.

POST /docs/convert           → Convert any document  { "url": str, "output_format": "json"|"markdown"|"html"|"text"|"doctags", "max_pages": int|null }
POST /docs/convert-ocr       → Forced OCR for scanned docs/images  { "url": str, "output_format": "json" }
POST /docs/chunk             → Convert + chunk for RAG  { "url": str, "max_tokens": 512, "merge_peers": true }
                               Returns: { "chunks": [{ "index": int, "text": str, "headings": [str], "page": int }] }
POST /docs/convert-vlm       → VLM pipeline for complex layouts  { "url": str, "output_format": "markdown" }
POST /docs/transcribe        → Audio/video transcription  { "url": str, "output_format": "markdown" }
                               Returns timestamped: [time: 0.0-4.0] Text here...
POST /docs/transcribe-diarized → Transcribe with speaker identification  { "url": str }
                               Returns: { "parser": "whisperx", "language": str, "num_speakers": int, "diarization_enabled": bool,
                                          "segments": [{ "start": float, "end": float, "text": str, "speaker": "SPEAKER_00",
                                                         "words": [{ "word": str, "start": float, "end": float, "score": float }] }] }
POST /docs/extract-metadata  → Format-specific metadata  { "url": str }
                               PDF: bookmarks, links, form_fields, metadata (title, author, creator, creation_date)
                               HTML: title, links, images, headings, embedded_media (iframes, videos, audio)
                               PPTX: slides with speaker_notes, metadata
                               DOCX: tracked_changes, metadata

Picture elements (in convert/convert-ocr/convert-vlm responses) carry:
  { "type": "picture",
    "page": int,
    "image_data": "<base64>",             # v1 only — inline base64 payload
    "image_format": "png"|"jpg"|"webp",
    "caption": "Figure 3: ...",           # NEW — human-readable caption if detected
    "classification": "diagram"|"chart"|"photo"|"logo"|...,   # NEW — image type
    "bbox": [x1, y1, x2, y2],             # NEW — position on the page
    "description": "...",                 # VLM-generated description (convert-vlm only)
    "confidence": float }

### DOCLING v2 (recommended for image-heavy documents) — /api/v2/docs/*
Same 7 endpoints, same request shapes, same 202-async contract as /api/v1/docs/*.
DIFFERENCE: extracted pictures are uploaded to a platform-managed MinIO bucket and returned as
presigned URLs instead of inline base64. This prevents MongoDB 16MB document-size failures on
image-heavy PDFs (e.g., 100+ page decks with embedded diagrams).

  POST /api/v2/docs/convert              (same body as v1)
  POST /api/v2/docs/convert-ocr          (same body as v1)
  POST /api/v2/docs/chunk                (same body as v1)
  POST /api/v2/docs/convert-vlm          (same body as v1)
  POST /api/v2/docs/transcribe           (same body as v1)
  POST /api/v2/docs/transcribe-diarized  (same body as v1)
  POST /api/v2/docs/extract-metadata     (same body as v1)
  GET  /api/v2/jobs/{job_id}             (v2-namespaced polling)

Picture elements in v2 responses replace image_data with:
  { "type": "picture",
    "page": int,
    "image_url": "https://<gpu-server>/tensornex-doc-pictures/extracted/<customer_id>/<job_id>/img_0001.png?X-Amz-...",
    "image_url_expires_in": 604800,       # seconds (default 7 days, MinIO max)
    "image_object_key": "extracted/<customer_id>/<job_id>/img_0001.png",
    "image_format": "png",
    "caption": "...", "classification": "...", "bbox": [...], "confidence": float
    # NOTE: image_data is ABSENT in v2
  }

When to use which:
  Text-heavy docs, few images     → v1 (simpler, no URL expiry to manage)
  Image-heavy docs (>50 pictures) → v2 (avoids Mongo 16MB failures)
  Building RAG with image refs    → v2 (durable URLs are easier to store & share)
  Legacy client expecting base64  → v1 (byte-for-byte unchanged)

### WEBHOOKS — /api/v1/webhooks (require API key auth)
Register a URL to receive HTTP POST notifications when async jobs reach a terminal state.
Your agent submits a job, gets back a job_id, then your webhook handler resumes processing when the job completes — no polling needed.

POST /webhooks                → Register webhook  { "url": "https://...", "events": ["job.completed", "job.failed", "job.cancelled"] }
                                Returns: { "id": str, "secret": "whsec_...", "url": str, "events": [...] }
                                Secret shown once — save it for signature verification.
GET  /webhooks                → List your webhooks (without secrets)
GET  /webhooks/{id}/deliveries → Recent delivery attempts with status codes
PUT  /webhooks/{id}           → Update URL or events  { "url": str, "events": [...] }
POST /webhooks/{id}/rotate-secret → Generate new secret (old one invalidated immediately)
DELETE /webhooks/{id}         → Delete a webhook

Payload format:
  { "event": "job.completed", "timestamp": ISO8601, "delivery_id": "wd_...",
    "data": { "job_id": "job_...", "service": str, "status": str, "result": {...} or "error": {...} } }

Signature: HMAC-SHA256 of the payload body using the webhook secret.
  Header: X-Tensornex-Signature: sha256=<hex>

Retry: 5 attempts (30s, 2min, 10min, 1h, 4h). Auto-deactivates after 20 consecutive failures.
URL must be HTTPS. No private IPs.

### ASYNC JOBS — All Docling endpoints return 202

All /docs/* endpoints (v1 AND v2) are ASYNCHRONOUS. They return HTTP 202 with a job_id immediately.
The actual processing happens in the background.

POST /docs/convert (and all /docs/* endpoints) → Returns 202:
  { "job_id": "job_...", "status": "pending",
    "poll_url": "/api/v1/jobs/job_..." | "/api/v2/jobs/job_...",  # matches the version you called
    "message": "Job accepted." }

  IMPORTANT: the poll_url is namespaced per version.
  - If you POST to /api/v1/docs/convert → poll /api/v1/jobs/{job_id}
  - If you POST to /api/v2/docs/convert → poll /api/v2/jobs/{job_id}
  Just follow the poll_url the API hands you and it will always be correct.

GET  /jobs/{job_id}           → Get job status + result (auth: same API key that created it)
                                Status: pending → running → completed | failed | cancelled
                                When completed: { "status": "completed", "api_version": "v1"|"v2", "result": { ... } }
                                When failed:    { "status": "failed", "error": { "message": "...", "type": "..." } }
GET  /jobs?status=pending&service=docling&page=1 → List your jobs (paginated, filterable)
DELETE /jobs/{job_id}         → Cancel a pending/running job (refunds reserved cost)

Cost: Reserved at submission, refunded on failure/cancellation. Max 10 concurrent jobs per account.
Webhooks: If registered, you receive a POST when job reaches terminal state — no polling needed.

### USAGE & LIMITS — GET /api/usage/summary (auth: JWT or API key)

Returns your current account tier, subscribed services, storage used, request counts, and active job counts.
Useful for building an in-app "quota widget" or deciding whether to submit a big batch.

  {
    "account_tier": "priority",              # your account-level tier (Phase 8)
    "enabled_services": ["docling", "qdrant", "bge_m3", "minio", "spacy"],
    "storage": {
      "used_bytes": 12884901888,             # ~12 GB
      "limit_bytes": 107374182400,           # 100 GB (per tier)
      "percent_used": 12
    },
    "requests_today": {
      "used": 4230,
      "limit": 50000,                        # daily cap per tier
      "resets_at": "2026-05-27T00:00:00Z"
    },
    "active_jobs": {
      "current": 3,
      "limit": 10                            # concurrent job cap
    }
  }

### PLATFORM HEALTH — GET /api/v1/health (no auth required)

Returns system status. Always HTTP 200 — read the "status" field.
  { "status": "healthy"|"degraded"|"down", "gpu_server": { "reachable": bool }, "queue": { "pending_jobs": int } }
healthy = all systems go. degraded = partial issues, requests still work. down = pause submissions.
Use this in circuit breakers: check health before submitting batches.

### RATE LIMITS & QUOTAS

Edge rate limit: 100 req/s per IP, burst 200. Response: HTTP 503 (back off and retry).
Concurrent async jobs: 10 per account. Response: HTTP 429 (queue client-side).
Max upload: 25 GB per request. Response: HTTP 413.

Async job retries (handled by platform automatically):
  - Service saturated (503): 5 attempts — 5s, 15s, 30s, 60s, 120s
  - Transient error: 3 attempts — 30s, 60s, 120s
  - Hard failure (4xx): not retried

Webhook delivery retries: 5 attempts — 30s, 2min, 10min, 1h, 4h.
After 20 consecutive webhook failures, webhook auto-deactivates.

Best practices:
  - Honor 503: back off with exponential delay + jitter
  - Don't retry 4xx: fix the request instead
  - Track job count: queue client-side if you need > 10 concurrent
  - Contact support@tensornex.cloud for higher limits`;

  const decisionTree = `## DECISION TREE: Which service(s) to use?

TASK: "I need to search text by meaning/similarity"
  → Enable: bge_m3 + qdrant
  → Flow: embed text → store in qdrant → embed query → search qdrant
  → Optional: add bge_reranker for precision

TASK: "I need keyword/full-text search"
  → Enable: elasticsearch
  → Flow: create index → index documents → search with query DSL

TASK: "I need both semantic AND keyword search"
  → Enable: bge_m3 + qdrant + elasticsearch + bge_reranker
  → Flow: embed+store in qdrant, index in elasticsearch, search both, rerank combined

TASK: "I need to extract entities/relationships from text"
  → Enable: spacy + neo4j
  → Flow: run NLP → create graph nodes/edges from entities

TASK: "I need caching / session storage"
  → Enable: redis
  → Flow: set/get keys with TTL

TASK: "I need event streaming / message queue"
  → Enable: kafka
  → Flow: create topic → produce messages → consume messages

TASK: "I need file/object storage"
  → Enable: minio
  → Flow: create bucket → upload files directly via /storage/upload → download via /storage/download
  → Note: Presigned URLs use internal hostname — use /storage/upload and /storage/download instead

TASK: "I need a complete RAG pipeline"
  → Enable: bge_m3 + qdrant + bge_reranker
  → Flow: embed docs → store vectors → embed query → search → rerank → feed to LLM

TASK: "I need a knowledge graph from documents"
  → Enable: spacy + neo4j + redis
  → Flow: extract entities (spaCy) → store relationships (Neo4j) → cache results (Redis)

TASK: "I need AI-powered web search with answers"
  → Enable: vane_search
  → Flow: send query → get cited answer from multiple web sources
  → LLM model depends on tier (configured by admin)

TASK: "I need raw web search results"
  → Enable: searxng
  → Flow: send query → get results from Google, Bing, DuckDuckGo etc.

TASK: "I need to read/scrape a web page"
  → Enable: lightpanda_browser
  → Flow: send URL → get rendered HTML + extracted text (handles JavaScript)

TASK: "I need an AI research agent"
  → Enable: vane_search + lightpanda_browser + qdrant + bge_m3
  → Flow: AI search → browse top results → embed content → store in vector DB

TASK: "I need to convert a document (PDF, DOCX, PPTX, XLSX, HTML, etc.)"
  → Enable: docling
  → Flow: upload to MinIO → get presigned URL → POST /docs/convert → get structured output
  → Supports: PDF, DOCX, PPTX, XLSX, HTML, CSV, Markdown, LaTeX, images, XBRL, JATS, USPTO, WebVTT

TASK: "I'm processing an image-heavy PDF (many diagrams, charts, or embedded pictures)"
  → Enable: docling + minio
  → USE: POST /api/v2/docs/convert  (NOT v1 — v1 returns inline base64 which risks MongoDB 16MB failures)
  → Response has "image_url" (presigned, valid 7 days) instead of "image_data" (base64)
  → Store the image_url in your DB or download the image while the URL is live
  → If the URL expires, re-run the job — same file will get a fresh presigned URL

TASK: "I want the same code to work for image-light AND image-heavy PDFs"
  → Default to /api/v2/docs/* — it works for both.
  → v2 with a no-picture document returns byte-for-byte the same as v1.
  → Only use v1 if you have a legacy client that expects inline base64.

TASK: "I need to OCR a scanned document or image"
  → Enable: docling
  → Flow: POST /docs/convert-ocr → get text with confidence scores per element

TASK: "I need RAG-ready chunks from a document"
  → Enable: docling + bge_m3 + qdrant
  → Flow: POST /docs/chunk → get token-aware chunks with headings → embed → store in qdrant

TASK: "I need to transcribe audio or video"
  → Enable: docling
  → Flow: upload audio/video to MinIO → POST /docs/transcribe → get timestamped transcript
  → Supports: WAV, MP3, M4A, AAC, OGG, FLAC, MP4, AVI, MOV

TASK: "I need to transcribe with speaker identification (who said what)"
  → Enable: docling
  → Flow: POST /docs/transcribe-diarized → get segments with speaker labels + word-level timestamps
  → Returns: speaker ID per segment, confidence per word, detected language

TASK: "I need PDF bookmarks, links, form fields, or PPTX speaker notes"
  → Enable: docling
  → Flow: POST /docs/extract-metadata → get format-specific metadata
  → PDF: bookmarks, link annotations, form fields, author, creation date
  → HTML: all links, images, headings, embedded media (iframes, videos)
  → PPTX: slide notes  |  DOCX: tracked changes, comments

TASK: "I need to process complex charts/diagrams in a PDF"
  → Enable: docling
  → Flow: POST /docs/convert-vlm → VLM reads visual layout intelligently

TASK: "I need a complete RAG pipeline from any document"
  → Enable: docling + bge_m3 + qdrant + bge_reranker
  → Flow: upload → /docs/chunk → embed chunks → store in qdrant → search → rerank

TASK: "I need to be notified when jobs complete instead of polling"
  → Register a webhook: POST /webhooks with your HTTPS URL and events
  → Flow: submit job → get job_id → your webhook handler fires when job completes
  → Your agent resumes processing from the webhook payload — no polling loop needed

TASK: "I'm calling a long-running endpoint (document conversion, transcription)"
  → All /docs/* endpoints are async — they return 202 with a job_id immediately
  → Your agent should: submit job → receive 202 → either poll GET /jobs/{id} or listen for webhook
  → DO NOT block waiting for the result — use the job_id to check later

TASK: "I need to check platform health before submitting a batch"
  → GET /api/v1/health (no auth needed)
  → If status="healthy" → proceed. If "degraded" → reduce rate. If "down" → pause.

TASK: "I'm hitting rate limits (429 or 503)"
  → 503 = edge rate limit (100 req/s per IP). Implement exponential backoff with jitter.
  → 429 = concurrent job limit (10 per account). Queue jobs client-side, submit as slots free up.
  → DO NOT retry 4xx errors (except 429) — those are request bugs, not rate limits.`;

  const errorHandling = `## ERROR HANDLING

All errors return JSON. There are two types: validation errors (your mistake — fixable) and processing errors (our side — tracked).

### Validation Errors (Status 400) — Fix and retry

"File type '.exe' is not supported. Supported formats: PDF, DOCX, PPTX, XLSX..."
  → You sent an unsupported file type. Use one of the listed formats.

"No URL provided. Upload a file to storage first, then pass the presigned URL."
  → The 'url' field is empty. Upload your file to /storage/upload first.

### Auth & Subscription Errors

Status 401: "API key required"
  → Missing or invalid X-API-Key header.

Status 403: "Service 'xxx' not in your current subscription."
  → Subscribe to this service from the dashboard first.

Status 402: "Insufficient wallet balance."
  → Add credit to your wallet.

Status 429: "Daily limit for {service} ({tier}): X requests/day."
  → Wait until midnight UTC or upgrade tier.

### Processing Errors (Status 502/504) — Tracked with reference ID

When a request fails on the server side, you get a structured response:
{
  "error": "processing_failed",
  "message": "Your request could not be processed. Our team has been notified.",
  "reference_id": "ERR-20260425-A3X7",
  "suggestions": ["Try a different file or format", "Try again in a few minutes", "Contact support with reference ID ERR-20260425-A3X7"]
}

  → Save the reference_id — use it when contacting support.
  → The team is automatically notified and can see your exact request details.
  → Common causes: file too large, corrupted file, GPU overloaded, unsupported content.

### v2-Specific Error: STORAGE_UNAVAILABLE (async job "failed" state)

Only appears on /api/v2/docs/* jobs. Means the platform's MinIO bucket was unreachable AND the
result document (all base64 images + text) was too large to safely fall back to inline base64
without risking a MongoDB 16 MB document-size failure.

On GET /api/v2/jobs/{id} you will see:
{
  "status": "failed",
  "api_version": "v2",
  "error": {
    "type": "STORAGE_UNAVAILABLE",
    "message": "MinIO bucket unavailable AND base64 fallback unsafe (result 15,320,102 bytes > limit 14,680,064 bytes)",
    "total_bytes": 15320102,
    "limit_bytes": 14680064
  }
}

  → Cost is auto-refunded to your wallet — you don't pay for this failure.
  → Recommended retry: wait 30-60s and re-submit the SAME job to /api/v2/docs/* — usually MinIO
    is back within a minute. If it keeps failing, contact support (transient infra issue).
  → For a one-time bypass: retry to /api/v1/docs/* if you're OK with base64 (accepting the risk
    that Mongo may reject payloads >16MB — you'll get a 502 with reference_id in that case).

### How to handle errors in code:

def call(method, path, json=None):
    resp = requests.request(method, f"{BASE}{path}", headers=HEADERS, json=json)
    if resp.status_code == 400:
        raise Exception(f"Validation error: {resp.json()['detail']}")  # Fix your input
    if resp.status_code == 403:
        raise Exception(f"Not subscribed: {resp.json()['detail']}")
    if resp.status_code in (502, 504):
        data = resp.json().get('detail', {})
        ref_id = data.get('reference_id', 'unknown') if isinstance(data, dict) else 'unknown'
        raise Exception(f"Processing failed (ref: {ref_id}). Try again or contact support.")
    resp.raise_for_status()
    return resp.json()`;

  const setupCode = `## COMPLETE SETUP CODE (Python)

import requests

# Configuration
BASE = "${API_BASE}/api/v1"
API_KEY = "gs_live_YOUR_KEY_HERE"
HEADERS = {
    "X-API-Key": API_KEY,
    "Content-Type": "application/json"
}

def call(method, path, json=None):
    """Universal helper — one function for all services."""
    url = f"{BASE}{path}"
    resp = requests.request(method, url, headers=HEADERS, json=json)
    if resp.status_code == 403:
        raise Exception(f"Service not enabled: {resp.json()['detail']}")
    if resp.status_code == 429:
        raise Exception(f"Service limit reached: {resp.json()['detail']}")
    if resp.status_code == 504:
        raise Exception(f"Timeout: {resp.json()['detail']}")
    resp.raise_for_status()
    return resp.json()

# ── Examples ──

# Embed
embeddings = call("POST", "/embeddings/embed", {
    "texts": ["Hello world"], "model": "bge_m3"
})

# Vector search
results = call("POST", "/vectors/search", {
    "collection_name": "docs", "vector": embeddings["data"]["dense"][0], "limit": 5
})

# Full-text search
hits = call("POST", "/search/query", {
    "index_name": "articles", "query": {"match": {"content": "machine learning"}}, "size": 10
})

# Graph query
graph = call("POST", "/graph/query", {
    "cypher": "MATCH (n:Entity) RETURN n LIMIT 10", "parameters": {}
})

# Cache
call("POST", "/cache/set", {"key": "result", "value": {"data": "cached"}, "ttl_seconds": 3600})
cached = call("POST", "/cache/get", {"key": "result"})

# Stream
call("POST", "/streaming/produce", {"topic": "events", "key": "k1", "value": {"event": "done"}})

# Storage (MinIO) — direct upload/download
call("POST", "/storage/buckets", {"bucket_name": "my-data"})

# Upload: use requests directly (multipart form data)
import requests as _req
with open("report.pdf", "rb") as f:
    _req.post(f"{BASE}/storage/upload",
        headers={"X-API-Key": API_KEY},
        data={"bucket_name": "my-data", "object_name": "reports/report.pdf"},
        files={"file": ("report.pdf", f, "application/pdf")}
    )

# Download
resp = _req.get(f"{BASE}/storage/download/my-data/reports/report.pdf", headers=HEADERS)
with open("downloaded.pdf", "wb") as f:
    f.write(resp.content)

# List objects
objects = call("POST", "/storage/list", {"bucket_name": "my-data", "prefix": "", "max_keys": 100})

# AI Search (Vane) — returns researched answer with source citations
answer = call("POST", "/search/ai-query", {
    "query": "What are the latest AI regulations?", "mode": "balanced"
})

# Raw Web Search (SearXNG) — raw results from multiple search engines
results = call("POST", "/search/raw", {"query": "machine learning", "limit": 10})

# Headless Browser (Lightpanda) — render JavaScript-heavy pages
page = call("POST", "/browse/render", {"url": "https://example.com", "timeout_ms": 30000})

# Extract specific content with CSS selectors
extracted = call("POST", "/browse/extract", {
    "url": "https://example.com", "selectors": {"title": "h1", "content": "article"}
})

# PDF Parser (LEGACY — /pdf/* still works, but Docling is the modern universal parser)
# For new integrations prefer /docs/convert — it handles PDF plus 20+ other formats.
doc = call("POST", "/pdf/parse", {"url": "https://example.com/document.pdf"})
doc_ocr = call("POST", "/pdf/parse-ocr", {"url": "https://example.com/scanned.pdf"})
tables = call("POST", "/pdf/extract-tables", {"url": "https://example.com/report.pdf"})

# Docling v1 (Universal Document AI) — convert any document format
# Returns inline base64 for pictures — best for text-heavy documents.
doc = call("POST", "/docs/convert", {
    "url": "https://example.com/report.docx", "output_format": "markdown"
})
job_id = doc["job_id"]  # async — poll doc["poll_url"] = /api/v1/jobs/{id}

# Docling v2 — same endpoints, but pictures returned as presigned URLs instead of base64
# STRONGLY RECOMMENDED for image-heavy PDFs (>50 pictures) to avoid MongoDB 16MB failures.
# The v2 endpoint lives at /api/v2 — swap the version in the URL, keep everything else.
BASE_V2 = "${API_BASE}/api/v2"

resp = requests.post(f"{BASE_V2}/docs/convert", headers=HEADERS, json={
    "url": presigned_url, "output_format": "json"
})
job = resp.json()
# {"job_id": "job_ab12...", "poll_url": "/api/v2/jobs/job_ab12...", "status": "pending"}

# Poll — always follow the poll_url returned in the 202 response
import time
while True:
    r = requests.get(f"${API_BASE}{job['poll_url']}", headers=HEADERS).json()
    if r["status"] == "completed":
        # Picture elements now carry image_url + image_object_key + image_url_expires_in.
        # image_data is ABSENT in v2 responses.
        for el in r["result"]["elements"]:
            if el["type"] == "picture":
                print(el["image_url"])          # presigned MinIO URL, valid 7 days
                print(el["caption"], el["classification"], el["bbox"])
        break
    if r["status"] == "failed":
        err = r["error"]
        if err["type"] == "STORAGE_UNAVAILABLE":
            # v2-only: platform storage down + fallback unsafe. Retry in 60s.
            print(f"Storage transient — total={err['total_bytes']} limit={err['limit_bytes']}")
        else:
            print(f"Failed: {err['message']}")
        break
    time.sleep(3)

# Other Docling operations (both v1 and v2 support all 7):
# OCR scanned document
ocr = call("POST", "/docs/convert-ocr", {"url": presigned_url})

# Chunk for RAG (token-aware)
chunks = call("POST", "/docs/chunk", {"url": presigned_url, "max_tokens": 512})

# VLM for complex layouts
vlm = call("POST", "/docs/convert-vlm", {"url": presigned_url, "output_format": "markdown"})

# Transcribe audio/video
transcript = call("POST", "/docs/transcribe", {"url": presigned_url, "output_format": "markdown"})

# Transcribe with speaker diarization (who said what)
diarized = call("POST", "/docs/transcribe-diarized", {"url": presigned_url})

# Extract format-specific metadata (PDF bookmarks, HTML links, PPTX notes)
meta = call("POST", "/docs/extract-metadata", {"url": presigned_url})

# Register a webhook to receive job completion notifications
webhook = call("POST", "/webhooks", {
    "url": "https://your-server.com/tensornex-webhook",
    "events": ["job.completed", "job.failed"]
})
# Save webhook["secret"] — needed to verify signatures
# Your webhook handler receives: {"event": "job.completed", "data": {"job_id": "...", "result": {...}}}

# Check YOUR usage and limits (helpful before big batches)
usage = call("GET", "/../usage/summary")  # actually GET /api/usage/summary (not under /v1)
# {"account_tier": "priority", "enabled_services": [...],
#  "storage": {"used_bytes": ..., "limit_bytes": ..., "percent_used": 12},
#  "requests_today": {"used": 4230, "limit": 50000, "resets_at": "..."},
#  "active_jobs": {"current": 3, "limit": 10}}

# Check platform health (no auth needed)
import requests as _r
health = _r.get(f"{BASE}/health").json()
# health["status"]: "healthy" | "degraded" | "down"`;

  return (
    <div data-testid="agent-guide-page" className="light-page min-h-screen bg-white text-slate-900">
      <PublicNav />
      <div className="pt-24 pb-16 px-5 max-w-5xl mx-auto space-y-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <img src="/tensornex-logo.png" alt="Tensornex" className="w-10 h-10 object-contain" />
            <div>
              <h1 className="text-3xl font-extrabold tracking-tight text-slate-900" style={{fontFamily: "'Plus Jakarta Sans', sans-serif"}}>
                AI Coding Agent Guide
              </h1>
              <p className="text-slate-500">Machine-readable integration spec for LLM-based coding agents</p>
            </div>
          </div>
          <p className="text-sm text-slate-600 mt-3 p-4 rounded-xl bg-indigo-50 border border-indigo-100">
            <strong>For AI coding agents (Cursor, Copilot, Claude, GPT, etc.):</strong> Copy the sections below into your system prompt or context window. 
            This guide is structured for how LLMs parse instructions — decision trees, not paragraphs.
          </p>
        </div>

        <Card className="border-slate-200 bg-white shadow-sm">
          <CardHeader><CardTitle className="text-lg flex items-center gap-2 text-slate-900"><Terminal className="w-4 h-4 text-indigo-600" /> System Prompt Snippet</CardTitle></CardHeader>
          <CardContent><Code code={systemPromptSnippet} title="Copy this into your agent's context" /></CardContent>
        </Card>

        <Card className="border-slate-200 bg-white shadow-sm">
          <CardHeader><CardTitle className="text-lg text-slate-900">Complete Endpoint Reference</CardTitle></CardHeader>
          <CardContent><Code code={endpointSpec} title="All 40+ endpoints (v1 + v2 Docling + usage summary) in one block" /></CardContent>
        </Card>

        <Card className="border-slate-200 bg-white shadow-sm">
          <CardHeader><CardTitle className="text-lg text-slate-900">Decision Tree: Which Services to Use</CardTitle></CardHeader>
          <CardContent><Code code={decisionTree} title="Task → Services → Flow" /></CardContent>
        </Card>

        <Card className="border-slate-200 bg-white shadow-sm">
          <CardHeader><CardTitle className="text-lg text-slate-900">Error Handling</CardTitle></CardHeader>
          <CardContent><Code code={errorHandling} title="Every error code and what to do" /></CardContent>
        </Card>

        <Card className="border-slate-200 bg-white shadow-sm">
          <CardHeader><CardTitle className="text-lg text-slate-900">Complete Setup Code</CardTitle></CardHeader>
          <CardContent><Code code={setupCode} title="Python — copy-paste ready" /></CardContent>
        </Card>

        <div className="p-5 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-600 space-y-2">
          <p className="font-bold text-slate-900">Key things for agents to remember:</p>
          <ul className="space-y-1">
            <li>&#8226; <strong>No client libraries</strong> — no pip install qdrant-client, no redis-py, no kafka-python. Just <code className="bg-white px-1 rounded text-xs">requests</code>.</li>
            <li>&#8226; <strong>One auth method</strong> — <code className="bg-white px-1 rounded text-xs">X-API-Key: gs_live_xxx</code> header on every request.</li>
            <li>&#8226; <strong>Namespace is automatic</strong> — pass <code className="bg-white px-1 rounded text-xs">"my_docs"</code>, the platform handles <code className="bg-white px-1 rounded text-xs">"client_abc123_my_docs"</code>.</li>
            <li>&#8226; <strong>Enable before use</strong> — calling a disabled service returns 403. Enable it first via the dashboard or API.</li>
            <li>&#8226; <strong>No infrastructure</strong> — no Docker, no ports, no config files. Sign up → enable → call.</li>
          </ul>
        </div>
      </div>
      <PublicFooter footer={footer} />
    </div>
  );
}
